import { Injectable, OnDestroy } from '@angular/core';
import {
	AbstractControl,
	FormBuilder,
	FormControl,
	Validators,
} from '@angular/forms';
import { DcForm } from '@datachain/ui-sdk/common';
import {
	BehaviorSubject,
	defer,
	distinctUntilChanged,
	filter,
	map,
	Observable,
	ReplaySubject,
	startWith,
	takeUntil,
	tap,
} from 'rxjs';

import { ExportFileConfigurationEntity } from '../export-file-configuration/export-file-configuration.entity';
import { ExportPartitionConfigurationEntity } from '../export-partition-configuration/export-partition-configuration.entity';
import { ExportWriteModeConfigurationEntity } from '../export-write-mode-configuration/export-write-mode-configuration.entity';
import {
	ConnectorMode,
	ExportConnectorEntity,
} from './export-connector.entity';
import {
	ExportFormat,
	ExportTemplateDetailsEntity,
} from './export-template-details.entity';
import { ExportTemplateDetailsHelper } from './export-template-details.helper';
import { ExportTemplateFormControls } from './export-template-form-controls';
import { ExportTemplateFormHelper } from './export-template.form-helper';

export { ExportTemplateFormControls };

@Injectable()
export class ExportTemplateDetailsForm
	extends DcForm<ExportTemplateDetailsEntity>
	implements OnDestroy
{
	private readonly onDestroy$ = new ReplaySubject<void>(1);
	private id: number;
	public connector = new FormControl<ExportConnectorEntity | null>(
		{
			value: ExportConnectorEntity.localConnector(),
			disabled: false,
		},
		[Validators.required]
	);

	public exportType = new FormControl<ExportFormat | null>(
		{
			value: null,
			disabled: false,
		},
		[Validators.required]
	);

	public keepOriginalLabels = new FormControl<boolean>(
		{
			value: true,
			disabled: false,
		},
		[]
	);

	public compress = new FormControl<boolean>(
		{
			value: false,
			disabled: false,
		},
		[]
	);

	public path = new FormControl<string | null>(
		{
			value: null,
			disabled: true,
		},
		[Validators.required]
	);

	public partition = new FormControl<ExportPartitionConfigurationEntity | null>(
		{
			value: ExportPartitionConfigurationEntity.build(),
			disabled: false,
		}
	);

	public fileConfig = new FormControl<ExportFileConfigurationEntity | null>({
		value: null,
		disabled: false,
	});

	public writeMode = new FormControl<ExportWriteModeConfigurationEntity | null>(
		{
			value: null,
			disabled: false,
		}
	);

	public fileNamePatternPrediction = new FormControl<string>('');

	private readonly predictedFileName = new BehaviorSubject<string>('');

	public readonly predictedFileName$ = this.predictedFileName.asObservable();

	protected _form = this.fb.group({});

	public constructor(
		protected readonly fb: FormBuilder,
		public readonly exportTemplateDetailsHelper: ExportTemplateDetailsHelper
	) {
		super();
		this.id = -1;
		this.addControls();
		this.initListeners();
	}

	public populate(entity: ExportTemplateDetailsEntity): void {
		this.id = entity.id;
		return ExportTemplateFormHelper.buildWith(
			entity.connector.type,
			entity.outputType
		).populate(entity, this);
	}

	public extract(): ExportTemplateDetailsEntity {
		return ExportTemplateFormHelper.buildWith(
			this.connector.value?.type as ConnectorMode,
			this.exportType.value as ExportFormat
		).generate(this.id, this.form);
	}

	protected updateForm(
		connector: ExportConnectorEntity | null,
		exportType: ExportFormat | null
	): void {
		if (connector === null || exportType === null) {
			return;
		}
		const connectorType = connector.type;
		return ExportTemplateFormHelper.buildWith(connectorType, exportType).update(
			connector,
			exportType,
			this
		);
	}

	public ngOnDestroy(): void {
		this.onDestroy$.next();
		this.onDestroy$.complete();
	}

	public compareConnectors(
		c1: ExportConnectorEntity,
		c2: ExportConnectorEntity
	): boolean {
		return c1.compareTo(c2) === 0;
	}

	public updatePath(path: string): void {
		this.form.patchValue({
			[ExportTemplateFormControls.Path]: path,
		});
	}

	public updatePredictedFileName(val: string): void {
		this.writePredictedFileName(val);
		this.form
			.get(ExportTemplateFormControls.FileNamePatternPrediction)
			?.markAsTouched();
	}

	// Seeding a prediction that came in with the template, as opposed to one the
	// user's edits just produced: same write, but the field is not touched yet so
	// a stored value that fails the pattern does not greet the user with an error.
	public seedPredictedFileName(val: string): void {
		this.writePredictedFileName(val);
	}

	private writePredictedFileName(val: string): void {
		// Display only control: it is never read back by buildCommonConfig, and
		// emitting here would feed the prediction result straight back into the
		// valueChanges pipes that asked for it.
		this.form.patchValue(
			{
				[ExportTemplateFormControls.FileNamePatternPrediction]: val,
			},
			{
				emitEvent: false,
			}
		);
		// The preview is plain text in an OnPush template, so it cannot rely on the
		// forms module writing into a bound input. This subject is what marks the
		// view for check - the control itself stays silent on purpose.
		this.predictedFileName.next(val);
	}

	// Derived form state, not a change stream: it must have a value the moment
	// anyone subscribes. The details view is gated on a combineLatest that
	// includes this, so a source that only emits on change blanks the page.
	public get isConfigurable$(): Observable<boolean> {
		const isConfigurable = (type: ExportFormat | null): boolean =>
			type !== null &&
			type !== ExportFormat.Parquet &&
			type !== ExportFormat.JSON;
		// defer: the seed below must be read when someone subscribes, not when the
		// component assembles vo$ in its constructor. populate() runs from
		// ngOnChanges - after the constructor, before the template's async pipe
		// subscribes - and valueChanges is hot, so without defer the only emission
		// carrying the real export type is missed and the view keeps the stale
		// false it was seeded with.
		return defer(() =>
			this.exportType.valueChanges.pipe(
				// A null is the control being rebuilt, not a real answer: ignore it and
				// keep the last one. Mapping null to false here hides the whole
				// specific-config card whenever the control is momentarily cleared.
				filter((type): type is ExportFormat => type !== null),
				map(isConfigurable),
				startWith(isConfigurable(this.exportType.value)),
				distinctUntilChanged()
			)
		);
	}

	protected addControls(): void {
		this.form.addControl(
			ExportTemplateFormControls.ConnectorMode,
			this.connector
		);
		this.form.addControl(
			ExportTemplateFormControls.ExportType,
			this.exportType
		);
		this.form.addControl(
			ExportTemplateFormControls.KeepOriginalLabels,
			this.keepOriginalLabels
		);
	}

	protected initListeners(): void {
		const connectorCtrl = this.form.get(
			ExportTemplateFormControls.ConnectorMode
		) as AbstractControl;
		const exportTypeCtrl = this.form.get(
			ExportTemplateFormControls.ExportType
		) as AbstractControl;

		connectorCtrl.valueChanges
			.pipe(
				takeUntil(this.onDestroy$),
				distinctUntilChanged(),
				filter((val): val is ExportConnectorEntity => val),
				tap((connector) => {
					const _exportType =
						this.exportType.value === ExportFormat.None
							? ExportFormat.CSV
							: this.exportType.value;
					this.form
						.get(ExportTemplateFormControls.ExportType)
						?.patchValue(_exportType);
					this.exportTemplateDetailsHelper.setSelectedConnectorType(
						connector.type
					);
					this.exportTemplateDetailsHelper.setSelectedConnector(connector);
					this.updateForm(connector, _exportType);
				})
			)
			.subscribe();

		exportTypeCtrl.valueChanges
			.pipe(
				takeUntil(this.onDestroy$),
				distinctUntilChanged(),
				filter((val): val is ExportFormat => val !== null),
				tap((exportType) => {
					const _exportType =
						exportType === ExportFormat.None ? ExportFormat.CSV : exportType;
					this.updateForm(this.connector.value, _exportType);
				})
			)
			.subscribe();
	}

	public toggleControls(
		enable: boolean,
		...controlNames: Array<ExportTemplateFormControls>
	): void {
		if (enable) {
			return controlNames.forEach((c) => {
				if (!this.form.get(c)?.enabled) {
					this.form.get(c)?.enable();
				}
			});
		}
		return controlNames.forEach((c) => {
			if (!this.form.get(c)?.disabled) {
				this.form.get(c)?.disable();
			}
		});
	}

	public removeControls(
		...controlNames: Array<ExportTemplateFormControls>
	): void {
		controlNames.forEach((c) => {
			if (this.form.get(c)) {
				this.form.removeControl(c);
			}
		});
	}
}
